import Application from '@ember/application';
import loadInitializers from 'ember-load-initializers';
import * as Sentry from '@sentry/browser';
import { Ember as EmberIntegration } from '@sentry/integrations';
import Resolver from './resolver';
import config from './config/environment';
import environment from '@square/environment-utils/environment';
import { defineCustomElements } from '@market/web-components/loader';
import './scss/app.scss';

function getReleaseFromCommitSha(sha) {
  return `banking-savings-onboarding-app@${sha?.slice(0, 8)}`;
}

const env = environment();

// Sentry Configuration. See linked docs for more info
// https://docs.sentry.io/error-reporting/configuration/?platform=node
// https://docs.sentry.io/platforms/javascript/ember/
const release =
  config.environment === 'production'
    ? getReleaseFromCommitSha(env.version)
    : '';
Sentry.init({
  dsn: 'https://1778aa9c43bd4eb2a0cf2d317a088c7f@o160250.ingest.sentry.io/5355429',
  integrations: [
    new EmberIntegration(),
    new Sentry.Integrations.Breadcrumbs({
      // disable console breadcrumbs to avoid sending unfiltered data from wirejs logs
      console: false,
    }),
  ],
  environment: env.name,
  attachStacktrace: true,
  release,
  ignoreErrors: [
    // Safari <= 14
    /^TypeError: cancelled/,
    // Safari >= 15
    /^TypeError: Load failed/,
    // Firefox
    /^TypeError: NetworkError when attempting to fetch resource./,
    // Chrome
    /^TypeError: Failed to fetch/,
    // Undescriptive errors caused by navigating away, etc,.
    /^XhrError/,
    /^ChunkLoadError/,
  ],

  beforeSend(event, hint) {
    // If you return null, the event will be discarded.
    // Docs on filtering events: https://docs.sentry.io/platforms/javascript/configuration/filtering/

    // We do not want to send Sentry events if the environment is dev or test
    if (config.environment === 'development' && console) {
      // eslint-disable-next-line no-console
      console.error(hint.originalException);
      return null;
    } else if (config.environment === 'test') {
      return null;
    }

    // Check if it's a non-standard exception (not an Error object)
    if (
      hint &&
      hint.originalException &&
      !(hint.originalException instanceof Error)
    ) {
      const nonErrorException = hint.originalException;

      event.message = `Non-Error exception captured: ${
        nonErrorException.type || 'Unknown Type'
      }`;

      event.extra = {
        ...event.extra,
        originalException: nonErrorException,
        keys: Object.keys(nonErrorException).join(', '),
      };

      return event;
    }

    // https://github.com/getsentry/sentry-javascript/issues/2380#issuecomment-573877991
    const capturedObj = hint.originalException;
    if (
      capturedObj instanceof CustomEvent &&
      capturedObj.detail &&
      capturedObj.detail.reason &&
      capturedObj.detail.reason instanceof Error
    ) {
      Sentry.withScope(() => {
        // pull out the embedded Error object
        const buriedError = capturedObj.detail.reason;
        capturedObj.detail.reason = '<error object captured above>';

        // capture the rest of the CustomEvent's data as extra
        Sentry.setExtras({
          originalThrownObject: capturedObj,
        });

        // create a new event out of the real Error, annotated with the other data
        Sentry.captureException(buriedError);
      });

      // don't send the original, badly-structured event
      return null;
    } else if (event.exception && event.exception.values) {
      // Filter out the errors we want to ignore
      const filteredErrors = event.exception.values.filter((error) => {
        // There are issues in Ember with TransitionAborted being logged by Sentry even
        // when no error is thrown and there's no problem for the user.
        // It is thrown even if we are intentionally redirecting the user.
        // https://github.com/emberjs/ember.js/issues/12505
        // https://github.com/emberjs/ember.js/issues/5566
        // and
        // https://github.com/emberjs/data/blob/1ca87a20851d441a69d077811f6f014561fc725e/addon/adapters/errors.js#L218
        if (error.type === 'TransitionAborted') {
          return false;
        }

        // This comes from Request being used in our ES2 config/setup, when it is not
        // supported in older browsers.
        if (
          error.value === "'Request' is undefined" ||
          error.value === "Can't find variable: Request" ||
          error.value === 'Request is not defined' ||
          error.value === 'Undefined variable: Request'
        ) {
          return false;
        }

        // An ember-intl issue with iOS Safari 9 and old Android/Chrome. These browsers are not supported.
        if (
          error.value === "Can't find variable: Intl" ||
          error.value === 'Intl is not defined'
        ) {
          return false;
        }
        return true;
      });

      if (filteredErrors.length > 0) {
        return event;
      }
      return null;
    } else {
      return event;
    }
  },
});

// Define @market/web-components custom elements
defineCustomElements();

export default class App extends Application {
  modulePrefix = config.modulePrefix;
  podModulePrefix = config.podModulePrefix;
  Resolver = Resolver;
}

loadInitializers(App, config.modulePrefix);
